import { Suspense, useState, useEffect } from 'react';
import Image from 'next/image';
import useModal from '@carvana/ds-modal';
import { Chevron } from '@carvana/ds-icons';
import { breakpoint, icon } from '@carvana/ds-styles';
import Skeleton from '@carvana/ds-skeleton';
import SummaryTile from './SummaryTile';
import { TileCard, ImageWrapper, StyledLinkButton } from './SummaryTiles.styles';
import styles from './SummaryTiles.module.css';
import type { VehicleSummary } from 'api/GraphQL/queries/getVehicleSummaries';
import useImageQuery from 'shared/hooks/useImageQuery';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';
import { useMediaQuery } from 'shared/hooks/useMediaQuery';

type SummaryTilesModalProps = {
  summaries: VehicleSummary[];
};

// Loading placeholder for the modal content
const ModalContentSkeleton = () => (
  <>
    <TileCard $size="md">
      <Skeleton width="100%" height="120px" />
    </TileCard>
    <TileCard $size="md">
      <Skeleton width="100%" height="120px" />
    </TileCard>
  </>
);

const SummaryTilesModal = ({ summaries }: SummaryTilesModalProps) => {
  const { Modal, openModal } = useModal();
  const { recordActionClicked } = useAnalyticEvent();
  const tileCount = summaries.length + 1;

  const handleOpen = async () => {
    await recordActionClicked(
      'Landing Page - View your other vehicles',
      'View your other vehicles tile click',
      { summaryCount: tileCount },
      true
    );
    openModal();
  };

  // Fetch initial vehicle image
  const { year, make, model, trim, color } = summaries[0];
  const yearMakeModel = `${year} ${make} ${model}`;
  const { data: imageUrl } = useImageQuery({
    year: year || 0,
    make: make || '',
    model: model || '',
    trim: trim || '',
    color: color || '',
    isMobile: true
  });

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = !!imageUrl && !imageError;

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [imageUrl]);

  // Fallback to no-image variant if image doesn't load within 5 seconds
  useEffect(() => {
    if (!imageUrl || imageLoaded || imageError) return undefined;
    const timeout = setTimeout(() => setImageError(true), 5000);
    return () => clearTimeout(timeout);
  }, [imageUrl, imageLoaded, imageError]);

  const isMobile = useMediaQuery(`(max-width: ${breakpoint.sm})`);

  return (
    <>
      <TileCard onClick={handleOpen} $size="sm">
        <ImageWrapper
          $hasImage={showImage}
          $size={isMobile ? 'sm' : 'md'}
          $overrideImageBackground
        >
          {showImage && (
            <Image
              loader={({ src, width }) =>
                src.includes('?') ? `${src}&w=${width}` : `${src}?w=${width}`
              }
              src={imageUrl}
              alt={yearMakeModel}
              width={isMobile ? 48 : 64}
              height={isMobile ? 38 : 54}
              style={{ objectFit: 'cover' }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
        </ImageWrapper>
        <StyledLinkButton size={isMobile ? 'sm' : 'md'} onClick={handleOpen} underline={false}>
          View your {summaries.length} other vehicles
          <Chevron direction="right" color={icon.primary} size={isMobile ? 16 : 24} />
        </StyledLinkButton>
      </TileCard>
      <Modal
        modalHeader="Your other vehicles"
        desktopWidth="480px"
        classes={{ header: styles.modalHeader }}
      >
        <Suspense fallback={<ModalContentSkeleton />}>
          {summaries.map(summary => (
            // Always render the mobile version in the modal
            <SummaryTile
              key={summary.appraisalId}
              summary={summary}
              isMobile
              tileCount={tileCount}
            />
          ))}
        </Suspense>
      </Modal>
    </>
  );
};

export default SummaryTilesModal;
